import { useRef } from 'react';

/**
 * A fresh key for one `Idempotency-Key` header (CLAUDE.md invariant 10) — one call per user
 * action, never reused across a real retry of the same logical action, and never shared across
 * unrelated ones.
 *
 * `crypto.randomUUID()` THROWS in an insecure context (no HTTPS, and NOT literally the hostname
 * `localhost`) — this actually happened here: `acme.lvh.me` resolves to 127.0.0.1 but is not
 * `localhost` by name, so the browser does not grant it. It threw synchronously while BUILDING the
 * mutation's arguments, before any network request or React error boundary saw it, which is why
 * the New Claim panel silently did nothing on Submit rather than showing an error — the whole
 * think would fail this way in ANY non-HTTPS deployment a browser does not special-case, not only
 * in this dev setup. `crypto.getRandomValues` (used to build the fallback below) has no such
 * restriction.
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through — some engines expose the function but still throw when called outside a
      // secure context, rather than omitting it.
    }
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Last resort — not cryptographically random, but this key only needs to be unique enough to
  // dedupe one browser tab's own retried requests, not to resist an attacker guessing it.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * One `Idempotency-Key` per logical money/financial write (CLAUDE.md invariant 10). A retry of the
 * SAME action with the SAME body (the network dropped, the person pressed again after an error)
 * reuses the key, so the server returns the original result instead of paying — or creating — it
 * twice; a success, a definite server answer, or a different body, starts a new key.
 *
 * EXP-P0-5 — moved here (2026-10-06) from the travel money screens, which already had this right;
 * the reimbursement panels (`NewClaimPanel`, `RequestAdvancePanel`) generated a fresh key on every
 * button press instead, so a retry after a dropped connection submitted a second, duplicate claim
 * or advance rather than converging on the first. One hook, every screen that writes money.
 */
export function useIdempotencyKeys() {
  const last = useRef(new Map<string, { json: string; key: string }>());
  return {
    keyFor(action: string, body: unknown): { 'idempotency-key': string } {
      const json = JSON.stringify(body ?? null);
      const previous = last.current.get(action);
      if (previous && previous.json === json) return { 'idempotency-key': previous.key };
      const key = generateIdempotencyKey();
      last.current.set(action, { json, key });
      return { 'idempotency-key': key };
    },
    /** The action succeeded, or got a definite server answer either way: its next call is a new
     * write, not a retry of this one. */
    settle(action: string) {
      last.current.delete(action);
    },
  };
}
